import { type FC, type FormEvent, useCallback, useEffect } from "react";
import { type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Button } from "@/components/flexkit/Button";
import { AWSLogo } from "@/assets/logos/AWSLogo";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Label } from "@/components/podkit/forms/Label";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { useId, useState } from "react";
import { Input } from "@/components/podkit/forms/Input";
import { cn } from "@/components/podkit/lib/cn";
import { useOrganization } from "@/queries/organization-queries";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { useRunnerName } from "@/hooks/use-runner-name";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";

const regionsList = [
    { label: "EU (Frankfurt)", value: "eu-central-1" },
    { label: "EU (Ireland)", value: "eu-west-1" },
    { label: "EU (London)", value: "eu-west-2" },
    { label: "EU (Paris)", value: "eu-west-3" },
    { label: "EU (Spain)", value: "eu-south-2" },
    { label: "US East (N. Virginia)", value: "us-east-1" },
    { label: "US East (Ohio)", value: "us-east-2" },
    { label: "US West (N. California)", value: "us-west-1" },
    { label: "US West (Oregon)", value: "us-west-2" },
    { label: "Canada (Central)", value: "ca-central-1" },
    { label: "São Paulo (South America)", value: "sa-east-1" },
    { label: "Asia Pacific (Mumbai)", value: "ap-south-1" },
    { label: "Asia Pacific (Singapore)", value: "ap-southeast-1" },
    { label: "Asia Pacific (Sydney)", value: "ap-southeast-2" },
    { label: "Asia Pacific (Tokyo)", value: "ap-northeast-1" },
    { label: "Middle East (Tel Aviv)", value: "il-central-1" },
];

export const NewAWSRunnerForm: FC<
    {
        formId: string;
        onSubmit: (values: { name: string; region: string }) => Promise<void>;
        onDisabledChange: (disabled: boolean) => void;
    } & PropsWithClassName
> = ({ formId, onDisabledChange, className, onSubmit }) => {
    const { data: organization, isLoading: isLoadingOrg, isPending: isPendingOrg } = useOrganization();
    const inputNameID = useId();
    const inputRegionID = useId();

    const [region, setRegion] = useState<string>(regionsList[0]?.value);

    const label = regionsList.find((r) => r.value === region)?.label;
    const {
        name,
        setName,
        isLoading: isLoadingName,
    } = useRunnerName({
        baseName: label ? `AWS ${label}` : "AWS Runner",
        provider: RunnerProvider.AWS_EC2,
    });

    // Update disabled state when name changes
    useEffect(() => {
        onDisabledChange(!name);
    }, [name, onDisabledChange]);

    const handleRegionChange = useCallback(
        (value: string) => {
            setRegion(value);
        },
        [setRegion],
    );

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!organization || !name) {
                return;
            }
            await onSubmit({ name, region });
        },
        [organization, name, region, onSubmit],
    );

    if (isLoadingOrg || isPendingOrg) {
        return <></>;
    }

    return (
        <form name={formId} id={formId} onSubmit={handleSubmit}>
            <div className={cn("flex flex-col gap-2", className)}>
                <div className="flex flex-row gap-4 space-y-1">
                    <div className="flex-shrink-0 p-4">
                        <AWSLogo className="size-12" />
                    </div>
                    <div className="flex grow flex-col gap-2">
                        <div className="text-sm">
                            Run environments for your organization on an EC2 instance. We will help you setup your
                            CloudFormation stack.
                        </div>
                        <div>
                            <Button variant="primary" asChild>
                                <ExternalLink href="https://ona.com/docs/ona/runners/aws/overview" iconSize="sm">
                                    <span>View Documentation</span>
                                </ExternalLink>
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="space-y-1">
                    <Label htmlFor={inputRegionID}>AWS region</Label>
                    <Select name="aws_region" value={region || ""} onValueChange={handleRegionChange}>
                        <SelectTrigger id={inputRegionID} loading={false}>
                            <SelectValue placeholder={"Select a region"}>{region}</SelectValue>
                            <SelectContent>
                                <SelectGroup>
                                    {regionsList.map((region) => (
                                        <SelectItem key={region.value} value={region.value}>
                                            <div className="flex w-full flex-row items-center justify-between pr-10">
                                                <div>{region.label}</div>
                                                <div>{region.value}</div>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </SelectTrigger>
                    </Select>
                </div>
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <Label htmlFor={inputNameID}>Name</Label>
                        {isLoadingName && <IconSpinner size="sm" className="animate-spin" />}
                    </div>
                    <Input
                        id={inputNameID}
                        type="text"
                        name="runner_name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={isLoadingName}
                        className="max-w-full"
                    />
                </div>
                <div className="mt-4 text-xs">
                    Please note: AWS infrastructure usage for runners will appear on your regular AWS billing statement.
                </div>
            </div>
        </form>
    );
};
